// EP-133 §3b (T133-023) — week schedule data hooks. The weekly calendar
// renders shift blocks (BE TimesheetBlock) + scheduled visits (BE
// VisitorVisit) together on a 30-minute grid. Both endpoints already
// accept a [starts_from, starts_to) range so the week-boundary math is
// entirely on the FE.
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface ShiftBlock {
  id: number
  user_id: number
  user_name: string
  kind: 'shift'
  starts_at: string
  ends_at: string
  hours: number
}

export interface CalendarVisit {
  id: number
  status: 'booked' | 'arrived' | 'departed' | 'cancelled' | 'no_show'
  starts_at: string
  ends_at: string
  purpose: string | null
  property_id: number
  property_name: string
  unit_name: string | null
  visitor: {
    id: number
    first_name: string
    last_name: string
  }
  created_via: 'widget' | 'staff' | 'resident_liff'
}

// Monday 00:00 → next Monday 00:00 in the account's local timezone.
// The BE serializes UTC ISO; the FE converts with the user's local zone
// for display. Two ranges: shifts filtered by user_id, visits NOT filtered
// (one column shows ALL staff's visitors in the admin view).
//
// `visitsEnabled` (T141-001 survey finding (b)) carries the caller's
// visitor-management dual-gate check (mirrors TodayStrip.tsx: `featureFlags.
// visitorManagement && accountSettings?.settings?.visitor_management_enabled`).
// Shifts are never gated — `/api/timesheet_blocks` dropped
// VisitorManagementModuleGate server-side (T141-004c) and is open to every
// account. `/api/visitor_visits` did NOT drop its gate (EP-141 §9 Q-2), so
// without this an account with the module off would fire a 403 on this query
// on every page load and every week-nav. Defaults to true so any other
// caller of this hook is unaffected.
export function useWeekSchedule(weekStart: Date, userId: number | null | undefined, visitsEnabled: boolean = true) {
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekEnd.getDate() + 7)

  const shifts = useQuery({
    queryKey: ['timesheet_blocks', 'week', isoRange(weekStart, weekEnd), userId ?? 'all'],
    queryFn: async () => {
      const { data } = await api.get<{ data: ShiftBlock[] }>('/api/timesheet_blocks', {
        params: {
          starts_from: weekStart.toISOString(),
          starts_to: weekEnd.toISOString(),
          user_id: userId ?? undefined,
        },
      })
      return data.data
    },
    staleTime: 60 * 1000,
  })

  const visits = useQuery({
    enabled: visitsEnabled,
    queryKey: ['visitor_visits', 'week', isoRange(weekStart, weekEnd)],
    queryFn: async () => {
      const { data } = await api.get<{ data: Array<{
        id: number
        status: CalendarVisit['status']
        starts_at: string
        ends_at: string
        purpose: string | null
        property_id: number
        property_name: string
        unit_name: string | null
        visitor: { id: number; first_name: string; last_name: string }
        created_via: CalendarVisit['created_via']
      }> }>('/api/visitor_visits', {
        params: {
          starts_from: weekStart.toISOString(),
          starts_to: weekEnd.toISOString(),
          per_page: 200,
        },
      })
      return data.data
    },
    staleTime: 60 * 1000,
  })

  return { shifts, visits }
}

function isoRange(start: Date, end: Date): string {
  return `${start.toISOString()}_${end.toISOString()}`
}